/**
 * Typewriter animation for the hero code snippet.
 *
 * Ported from `typeText` (`public/index.html:759-777`) and the observer that
 * runs it once (`:779-805`): the three lines appear in turn at 30 ms / 40 ms /
 * 30 ms per character, with 800 ms before the reply and 500 ms before the
 * result, and each line's cursor is hidden when its text finishes.
 */
import { useEffect, useRef, useState } from 'react';

export interface TypedLine {
  text: string;
  speed: number;
  /** Delay before this line starts, measured from the previous line finishing. */
  delay: number;
}

export interface TypedLineState {
  visible: boolean;
  typed: string;
  done: boolean;
}

export const useTypedSequence = (lines: TypedLine[]) => {
  const ref = useRef<HTMLElement | null>(null);
  const [state, setState] = useState<TypedLineState[]>(() =>
    lines.map((_, i) => ({ visible: i === 0, typed: '', done: false }))
  );
  const started = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const timers: Array<ReturnType<typeof setTimeout>> = [];

    const typeLine = (index: number, onDone: () => void) => {
      const { text, speed } = lines[index];
      let i = 0;
      const step = () => {
        if (i < text.length) {
          i += 1;
          setState((prev) =>
            prev.map((l, j) =>
              j === index ? { ...l, typed: text.slice(0, i) } : l
            )
          );
          timers.push(setTimeout(step, speed));
          return;
        }
        setState((prev) =>
          prev.map((l, j) => (j === index ? { ...l, done: true } : l))
        );
        onDone();
      };
      step();
    };

    const runFrom = (index: number) => {
      if (index >= lines.length) return;
      setState((prev) =>
        prev.map((l, j) => (j === index ? { ...l, visible: true } : l))
      );
      typeLine(index, () => {
        const next = index + 1;
        if (next >= lines.length) return;
        timers.push(setTimeout(() => runFrom(next), lines[next].delay));
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || started.current) return;
          started.current = true;
          runFrom(0);
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
      timers.forEach(clearTimeout);
    };
  }, [lines]);

  return { ref, lines: state };
};
